import { useLocation } from 'react-router-dom';
import { ROOT } from './shared/Routes';
import { Helmet } from 'react-helmet';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAppMode, isWebsiteUnderMaintenance } from './shared/Utils';
import { useAuth } from './hooks/useAuth';
import { useBatchId } from './hooks/useBatchId';
import AppRoutes from './routes/AppRoutes';

interface UserLoggedOutEventDetail {
	loggedOut: boolean;
}
type UserLoggedOutEvent = CustomEvent<UserLoggedOutEventDetail>;

export default function App() {
	const { logout, isLoggedIn } = useAuth();
	const { navigateToRoute, queryParams } = useBatchId();
	const [underMaintenance, setUnderMaintenance] = useState(false);
	const authRef = useRef<HTMLElement | null>(null);
	const { pathname } = useLocation();
	const isNoCrawling =
		process.env.REACT_APP_APP_MODE === 'staging' ||
		process.env.REACT_APP_APP_MODE === 'development';

	useEffect(() => {
		isWebsiteUnderMaintenance()
			.then((res) => setUnderMaintenance(res))
			.catch(() => {});
	}, []);

	useEffect(() => {
		import('hfnauth/main');
	});

	const handleUserLoggedOut = useCallback(
		async (event: UserLoggedOutEvent) => {
			if (event?.detail?.loggedOut && logout) {
				await logout(true);
			}
		},
		[logout]
	);

	useEffect(() => {
		const element = document.querySelector('hfn-auth');
		if (element instanceof HTMLElement) {
			authRef.current = element;
		} else {
			authRef.current = null;
		}
		const listener = (event: Event) =>
			handleUserLoggedOut(event as UserLoggedOutEvent);
		if (authRef.current) {
			authRef.current.addEventListener(
				'userLoggedOut',
				listener as EventListener
			);
		}
		return () => {
			if (authRef.current) {
				authRef?.current?.removeEventListener(
					'userLoggedOut',
					listener as EventListener
				);
			}
		};
	}, [authRef, handleUserLoggedOut]);

	useEffect(() => {
		if (underMaintenance) {
			import('hfn-maintenance-web/main');
		}
	}, [underMaintenance]);

	useEffect(() => {
		import('hfn-appmode/main');
	}, []);

	useEffect(() => {
		if (pathname.includes('hfnauth') && isLoggedIn && isLoggedIn()) {
			['state', 'session_state', 'iss', 'code'].forEach((param) =>
				queryParams.delete(param)
			);
			navigateToRoute(ROOT);
		}
	}, [isLoggedIn, navigateToRoute, pathname, queryParams]);

	return (
		<>
			<hfn-auth
				ref={authRef}
				config={process.env.REACT_APP_KEYCLOAK_CONFIG}
				showCancel
				authType={!pathname.includes('hfnauth') ? 'on-demand' : ''}
			/>
			{isNoCrawling && (
				<Helmet>
					<meta name="robots" content="noindex" />
				</Helmet>
			)}
			<hfn-app-mode
				appMode={getAppMode(process.env.REACT_APP_APP_MODE)}
				displayOn="topLeft"
			/>

			{underMaintenance ? (
				<hfn-maintenance-web />
			) : (
				<div>
					<AppRoutes />
				</div>
			)}
		</>
	);
}
